import React, { useCallback, useState } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { Flyout, FlyoutContent } from "@amzn/stencil-react-components/flyout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { SdaHeader } from "./sda-header";
import { SdaDetails } from "./sda-details";
import { SdaChange } from "./sda-change";

export interface SdaDetails {
  errorCode?: number;
  startDate: string;
  startTime: string;
  publicText: string;
  address: string;
  startDateStatus: string;
}

interface SdaControlledProps {
  details?: SdaDetails;
  loading?: boolean;
  error?: boolean;
  onRetry: () => void;
  onViewDetailsClick: () => void;
  onChangeClick: () => void;
  onSupportLinkClick: () => void;
}

export const SdaControlled = ({
  details,
  onRetry,
  loading,
  error,
  onViewDetailsClick,
  onChangeClick,
  onSupportLinkClick,
}: SdaControlledProps) => {
  const [isChangeViewOpen, setIsChangeViewOpen] = useState(false);
  const [isDetailsViewOpen, setIsDetailsViewOpen] = useState(false);

  const openChangeView = useCallback(() => {
    setIsChangeViewOpen(true);
    onChangeClick();
  }, [setIsChangeViewOpen]);

  const openDetailsView = useCallback(() => {
    setIsDetailsViewOpen(true);
    onViewDetailsClick();
  }, [setIsDetailsViewOpen]);

  const closeChangeView = useCallback(() => {
    setIsChangeViewOpen(false);
  }, [setIsChangeViewOpen]);

  const closeDetailsView = useCallback(() => {
    setIsDetailsViewOpen(false);
  }, [setIsDetailsViewOpen]);

  if (loading) {
    return (
      <Spinner
        size={SpinnerSize.Small}
        loadingText="Loading..."
        showText={true}
      ></Spinner>
    );
  }

  if (error) {
    return null; // Remove and customize the error state if needed
    return (
      <Col gridGap="1rem">
        <MessageBanner type={MessageBannerType.Error}>
          Unable to retrieve the start date details. Please try again later by
          clicking the “Retry” button below.
        </MessageBanner>
        <Row>
          <Button size={ButtonSize.Small} onClick={onRetry}>
            Retry
          </Button>
        </Row>
      </Col>
    );
  }

  if (!details) {
    return null;
  }

  const startDateTime = details.startDate + " " + details.startTime;
  const vh = window.innerHeight + "px";

  return (
    <>
      <SdaHeader
        startDate={details.startDate}
        startTime={details.startTime}
        onViewDetailsClick={openDetailsView}
        onChangeClick={openChangeView}
        tentative={details.startDateStatus.toLocaleLowerCase() === "tentative"}
      ></SdaHeader>

      {/* Details view Flyout */}
      <Flyout isOpen={isDetailsViewOpen} close={closeDetailsView}>
        <div style={{ height: vh }}>
          <FlyoutContent
            titleText="First Day Onsite"
            buttons={[
              <Button
                key="close-button"
                variant={ButtonVariant.Primary}
                onClick={closeDetailsView}
              >
                Close
              </Button>,
            ]}
            onCloseButtonClick={closeDetailsView}
          >
            <SdaDetails
              startDateTime={startDateTime}
              address={details.address}
              publicText={details.publicText}
            ></SdaDetails>
          </FlyoutContent>
        </div>
      </Flyout>

      {/* Change view Flyout */}
      <Flyout isOpen={isChangeViewOpen} close={closeChangeView}>
        <div style={{ height: vh }}>
          <FlyoutContent
            titleText="Change your first day"
            onCloseButtonClick={closeChangeView}
            buttons={[
              <Button
                key="close"
                onClick={closeChangeView}
                variant={ButtonVariant.Primary}
              >
                Close
              </Button>,
            ]}
          >
            <SdaChange onSupportLinkClick={onSupportLinkClick} />
          </FlyoutContent>
        </div>
      </Flyout>
    </>
  );
};
